import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { colors } from '../../brand-colors';

const ASSET_TYPE_LABELS = {
  social_post: 'Social',
  long_form: 'Blog',
  short_video: 'Video',
  repost: 'Repost',
  curated: 'Curated',
};

const STATUS_COLORS = {
  published: colors.goldDark,
  approved: '#1E3A5F',
  pending_approval: '#ffc107',
  archived: colors.greyMid,
  expired: '#bfbfbf',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ContentCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const response = await fetch(`/api/weekly-assets/calendar?month=${monthStr}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load calendar');
      setData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const navigate = (direction) => {
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    if (newMonth < 1) { newMonth = 12; newYear--; }
    setMonth(newMonth);
    setYear(newYear);
    setSelectedDay(null);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let currentWeek = new Array(firstDay).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const getDayAssets = (day) => {
    if (!data?.days || !day) return [];
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return data.days[key] || [];
  };

  const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

  return (
    <Container fluid className="py-4" style={{ maxWidth: 1100 }}>
      <h3 style={{
        fontFamily: "'Trajan Pro', 'Times New Roman', serif",
        letterSpacing: '0.15em',
        color: colors.greyDarkest,
        marginBottom: 8,
      }}>
        CONTENT CALENDAR
      </h3>
      <p className="text-muted mb-4" style={{ fontSize: 14 }}>
        Your scheduled and published weekly marketing assets
      </p>

      {/* Summary bar */}
      {data?.summary && (
        <div className="d-flex gap-3 mb-3 flex-wrap" style={{ fontSize: 13 }}>
          <span><strong>{data.summary.total}</strong> total</span>
          <span style={{ color: STATUS_COLORS.published }}><strong>{data.summary.published}</strong> published</span>
          <span style={{ color: STATUS_COLORS.approved }}><strong>{data.summary.scheduled}</strong> scheduled</span>
          <span style={{ color: STATUS_COLORS.pending_approval }}><strong>{data.summary.pending}</strong> pending</span>
          {Object.entries(data.summary.by_type || {}).map(([type, count]) => (
            <span key={type} className="text-muted">{ASSET_TYPE_LABELS[type] || type}: {count}</span>
          ))}
        </div>
      )}

      {/* Month navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button variant="outline-dark" size="sm" onClick={() => navigate(-1)}>Previous</Button>
        <h5 style={{ margin: 0, fontWeight: 600 }}>{monthName} {year}</h5>
        <Button variant="outline-dark" size="sm" onClick={() => navigate(1)}>Next</Button>
      </div>

      {loading && <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        <>
          {/* Calendar grid */}
          <Card style={{ border: `1px solid ${colors.greyLight}` }}>
            <Card.Body className="p-0">
              {/* Day headers */}
              <Row className="g-0" style={{ borderBottom: `1px solid ${colors.greyLight}` }}>
                {DAY_NAMES.map(d => (
                  <Col key={d} style={{
                    textAlign: 'center',
                    padding: '8px 0',
                    fontWeight: 600,
                    fontSize: 12,
                    color: colors.greyMid,
                    background: '#f8f9fa',
                  }}>
                    {d}
                  </Col>
                ))}
              </Row>

              {/* Week rows */}
              {weeks.map((week, wi) => (
                <Row key={wi} className="g-0" style={{ borderBottom: wi < weeks.length - 1 ? `1px solid ${colors.greyLight}` : 'none' }}>
                  {week.map((day, di) => {
                    const dayAssets = getDayAssets(day);
                    const isSelected = selectedDay === day && day !== null;
                    const isToday = day === new Date().getDate() && month === new Date().getMonth() + 1 && year === new Date().getFullYear();

                    return (
                      <Col
                        key={di}
                        style={{
                          minHeight: 80,
                          padding: 6,
                          cursor: day && dayAssets.length > 0 ? 'pointer' : 'default',
                          background: isSelected ? '#f0ead6' : isToday ? '#fffbf0' : 'transparent',
                          borderRight: di < 6 ? `1px solid ${colors.greyLight}` : 'none',
                        }}
                        onClick={() => day && dayAssets.length > 0 && setSelectedDay(isSelected ? null : day)}
                      >
                        {day && (
                          <>
                            <div style={{
                              fontSize: 12,
                              fontWeight: isToday ? 700 : 400,
                              color: isToday ? colors.goldDark : colors.greyDarkest,
                              marginBottom: 4,
                            }}>
                              {day}
                            </div>
                            {dayAssets.length > 0 && (
                              <div className="d-flex flex-wrap gap-1">
                                {summarizeByType(dayAssets).map(({ type, count, status }) => (
                                  <Badge
                                    key={type + status}
                                    style={{
                                      fontSize: 9,
                                      padding: '2px 5px',
                                      background: STATUS_COLORS[status] || colors.greyMid,
                                      color: status === 'pending_approval' ? '#333' : '#fff',
                                    }}
                                  >
                                    {count} {ASSET_TYPE_LABELS[type]?.[0] || type[0]}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </Col>
                    );
                  })}
                </Row>
              ))}
            </Card.Body>
          </Card>

          {/* Expanded day view */}
          {selectedDay && (
            <Card className="mt-3" style={{ border: `1px solid ${colors.greyLight}` }}>
              <Card.Header style={{
                background: colors.greyDarkest,
                color: colors.goldLight,
                fontFamily: "'Trajan Pro', 'Times New Roman', serif",
                letterSpacing: '0.1em',
                fontSize: 13,
              }}>
                {monthName} {selectedDay}, {year}
              </Card.Header>
              <Card.Body className="p-0">
                {getDayAssets(selectedDay).map(asset => (
                  <div
                    key={asset.id}
                    className="d-flex align-items-center gap-3 px-3 py-2"
                    style={{ borderBottom: `1px solid ${colors.greyLight}`, fontSize: 13 }}
                  >
                    <Badge style={{
                      background: STATUS_COLORS[asset.status] || colors.greyMid,
                      color: asset.status === 'pending_approval' ? '#333' : '#fff',
                      fontSize: 10,
                      minWidth: 70,
                      textAlign: 'center',
                    }}>
                      {asset.status.replace('_', ' ')}
                    </Badge>
                    <Badge bg="dark" style={{ fontSize: 10 }}>
                      {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
                    </Badge>
                    <span className="flex-grow-1" style={{ color: '#444' }}>
                      {(asset.content?.title || asset.content?.text || '').slice(0, 100)}
                      {(asset.content?.title || asset.content?.text || '').length > 100 ? '...' : ''}
                    </span>
                    {asset.topic_score && (
                      <span style={{ fontWeight: 600, color: colors.goldDark }}>{asset.topic_score.toFixed(1)}</span>
                    )}
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}

function summarizeByType(assets) {
  const summary = {};
  for (const asset of assets) {
    const key = `${asset.asset_type}-${asset.status}`;
    if (!summary[key]) {
      summary[key] = { type: asset.asset_type, status: asset.status, count: 0 };
    }
    summary[key].count++;
  }
  return Object.values(summary);
}

export default ContentCalendar;
